import React, { useState, useEffect, useCallback } from "react";
import { Modal, Icon } from "semantic-ui-react";
import { toast } from "react-toastify";
import { EscalationRules } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useLanguage } from "../../../../contexts";
import "./EscalationRulesModal.scss";

const rulesController = new EscalationRules();

const PRIORITY_ROWS = [
  { key: "high",   labelKey: "erm_priority_high",   color: "#e53935" },
  { key: "medium", labelKey: "erm_priority_medium",  color: "#f9a825" },
  { key: "low",    labelKey: "erm_priority_low",     color: "#43a047" },
];

const HARDCODED_DEFAULTS = {
  high:   { rule_l1: 0, rule_l2: 2, rule_l3: 5  },
  medium: { rule_l1: 0, rule_l2: 3, rule_l3: 7  },
  low:    { rule_l1: 0, rule_l2: 5, rule_l3: 10 },
};

function buildEmpty() {
  return {
    high:   { ...HARDCODED_DEFAULTS.high   },
    medium: { ...HARDCODED_DEFAULTS.medium },
    low:    { ...HARDCODED_DEFAULTS.low    },
  };
}

function validateRow(key, row, t) {
  const l1 = Number(row.rule_l1);
  const l2 = Number(row.rule_l2);
  const l3 = Number(row.rule_l3);
  if ([l1, l2, l3].some((v) => isNaN(v) || !Number.isInteger(v))) return t("erm_val_integers");
  if ([l1, l2, l3].some((v) => v < 0))   return t("erm_val_positive");
  if ([l1, l2, l3].some((v) => v > 90))  return t("erm_val_max");
  if (l2 <= l1) return t("erm_val_l2_gt_l1");
  if (l3 <= l2) return t("erm_val_l3_gt_l2");
  return null;
}

export function EscalationRulesModal({ show, close }) {
  const { accessToken } = useAuth();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const [rules,   setRules]   = useState(buildEmpty);
  const [loading, setLoading] = useState(false);
  const [saving,  setSaving]  = useState(false);

  useEffect(() => {
    if (!show) return;
    setLoading(true);
    rulesController.getEscalationRules(accessToken)
      .then((data) => setRules(data))
      .catch(() => setRules(buildEmpty()))
      .finally(() => setLoading(false));
  }, [show, accessToken]);

  const setField = useCallback((priority, field, val) => {
    setRules((p) => ({
      ...p,
      [priority]: { ...p[priority], [field]: val === "" ? "" : Number(val) },
    }));
  }, []);

  const handleSave = async () => {
    for (const { key, labelKey } of PRIORITY_ROWS) {
      const err = validateRow(key, rules[key], t);
      if (err) { toast.error(`${t(labelKey)}: ${err}`); return; }
    }
    setSaving(true);
    try {
      await rulesController.updateEscalationRules(accessToken, rules);
      toast.success(t("erm_saved"));
      close();
    } catch (err) {
      toast.error(err?.msg || t("erm_error_save"));
    } finally { setSaving(false); }
  };

  const FIELD_LABELS = [
    { field: "rule_l1", label: t("erm_field_l1") },
    { field: "rule_l2", label: t("erm_field_l2") },
    { field: "rule_l3", label: t("erm_field_l3") },
  ];

  return (
    <Modal
      open={show}
      onClose={close}
      closeOnDimmerClick={false}
      centered={false}
      className="erm-modal"
    >
      <div className="erm-wrap">

        {/* ── Header ── */}
        <div className="erm-wrap__header">
          <span className="erm-wrap__header-title">{t("erm_title")}</span>
          <button className="erm-wrap__header-close" onClick={close}>
            <Icon name="close" />
          </button>
        </div>

        <div className="erm-wrap__body">
          {loading ? (
            <p className="erm__loading">{t("erm_loading")}</p>
          ) : (
            <div className="erm">
              <p className="erm__hint">{t("erm_hint")}</p>

              {PRIORITY_ROWS.map(({ key, labelKey, color }) => {
                const row = rules[key] || {};
                const err = validateRow(key, row, t);
                return (
                  <div key={key} className="erm__card">
                    <div className="erm__card-title" style={{ color }}>
                      {t(labelKey)}
                    </div>
                    <div className="erm__card-fields">
                      {FIELD_LABELS.map(({ field, label: flabel }) => (
                        <div key={field} className="erm__field-group">
                          <span className="erm__field-label">{flabel}</span>
                          <input
                            type="number"
                            min={0}
                            max={90}
                            className={`erm__input${err ? " erm__input--error" : ""}`}
                            value={row[field] ?? ""}
                            onChange={(e) => setField(key, field, e.target.value)}
                          />
                        </div>
                      ))}
                    </div>
                    {err && <p className="erm__row-error">{err}</p>}
                  </div>
                );
              })}

              <div className="erm__footer">
                <button className="erm__cancel-btn" onClick={close} disabled={saving}>
                  {t("erm_cancel")}
                </button>
                <button
                  className="erm__save-btn"
                  disabled={saving}
                  onClick={handleSave}
                >
                  {saving ? t("erm_saving") : t("erm_save_btn")}
                </button>
              </div>
            </div>
          )}
        </div>

      </div>
    </Modal>
  );
}
